import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react'
import { useRouter } from 'next/router'
import { UserContext } from '../../context/UserContext'
import {
    getMyCorporateSessions, getMySessionEnrollments,
    enrollCorporateSession, cancelSessionEnrollment
} from '../../api/corporate/corporate-api'
import { ProgramSection } from './ProgramSection'
import { SessionDetailSheet } from './SessionDetailSheet'
import { Building2, ArrowLeft, Dumbbell } from 'lucide-react'
import DotLoader from 'react-spinners/DotLoader'

export const ProgramsPage = () => {
    const router = useRouter()
    const { corporateProfile } = useContext(UserContext)

    const [sessions, setSessions] = useState([])
    const [enrollments, setEnrollments] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [selectedSession, setSelectedSession] = useState(null)

    const enrolledIds = useMemo(() => {
        return new Set(enrollments.filter(e => e.status === 'enrolled' || e.status === 'attended').map(e => e.sessionId))
    }, [enrollments])

    const sessionMap = useMemo(() => {
        const map = {}
        sessions.forEach(s => { map[s.id] = s })
        return map
    }, [sessions])

    const loadData = useCallback(async () => {
        try {
            setIsLoading(true)
            const [sessData, enrollRes] = await Promise.all([
                getMyCorporateSessions(),
                getMySessionEnrollments().catch(() => ({ data: [] }))
            ])
            setSessions(sessData || [])
            const enrollData = enrollRes?.data || enrollRes || []
            setEnrollments(Array.isArray(enrollData) ? enrollData : [])
        } catch (error) {
            console.log('Could not load programs:', error)
        } finally {
            setIsLoading(false)
        }
    }, [])

    useEffect(() => {
        if (corporateProfile) loadData()
        else setIsLoading(false)
    }, [corporateProfile, loadData])

    const handleEnroll = useCallback(async (sessionId) => {
        try {
            await enrollCorporateSession(sessionId)
            const sess = sessionMap[sessionId] || null
            setEnrollments(prev => [...prev, { sessionId, status: 'enrolled', enrolledAt: new Date().toISOString(), session: sess }])
        } catch (err) { console.log('Enroll failed', err) }
    }, [sessionMap])

    const handleCancelEnrollment = useCallback(async (sessionId) => {
        try {
            await cancelSessionEnrollment(sessionId)
            setEnrollments(prev => prev.filter(e => e.sessionId !== sessionId))
            setSelectedSession(null)
        } catch (err) { console.log('Cancel failed', err) }
    }, [])

    if (!corporateProfile) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
                <Building2 size={40} className="text-gray-300 mb-4" />
                <p className="text-gray-500 text-sm text-center">You are not enrolled in any corporate wellness programs.</p>
            </div>
        )
    }

    const programs = corporateProfile.programs || []

    return (
        <div className="min-h-screen">
            {/* Header */}
            <div className="bg-linear-to-r from-indigo-600 via-indigo-700 to-violet-700 px-4 md:px-8 py-5 rounded-b-2xl">
                <div className="flex items-center gap-3">
                    <button onClick={() => router.push('/corporate')} className="p-2 rounded-xl bg-white/15 hover:bg-white/25 transition">
                        <ArrowLeft size={18} className="text-white" />
                    </button>
                    <div className="flex items-center gap-2">
                        <Dumbbell size={18} className="text-white/80" />
                        <h1 className="text-lg font-bold text-white">Programs</h1>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="px-4 pt-4 pb-24 space-y-4">
                {isLoading ? (
                    <div className="flex justify-center py-12">
                        <DotLoader color="#6366f1" size={36} />
                    </div>
                ) : programs.length > 0 ? (
                    programs.map((program, idx) => (
                        <ProgramSection
                            key={program.programId || idx}
                            program={program}
                            sessions={sessions}
                            onSelectSession={setSelectedSession}
                        />
                    ))
                ) : (
                    <div className="text-center py-12">
                        <Dumbbell size={32} className="text-gray-300 mx-auto mb-3" />
                        <p className="text-sm text-gray-400">No programs found</p>
                    </div>
                )}
            </div>

            <SessionDetailSheet
                session={selectedSession}
                isOpen={!!selectedSession}
                onClose={() => setSelectedSession(null)}
                enrolledIds={enrolledIds}
                onEnroll={handleEnroll}
                onCancelEnrollment={handleCancelEnrollment}
            />
        </div>
    )
}
